import React from 'react';
import { MetricConfig } from '../../types/metrics';
import { useMetrics } from '../../hooks/useMetrics';
import { useStatCardsBatchContext } from '../../context/StatCardsBatchContext';
import { getMetricPathFromEndpoint } from '../../utils/metricPath';
import MetricRenderer from './MetricRenderer';
import CydCohortRateChart from './CydCohortRateChart';

interface MetricItemProps {
  config: MetricConfig;
  storeId: string | null;
  startDate?: string;
  endDate?: string;
}

const MetricItem: React.FC<MetricItemProps> = ({ 
  config, 
  storeId, 
  startDate, 
  endDate 
}) => {
  const batchContext = useStatCardsBatchContext();
  const metricPath = getMetricPathFromEndpoint(config.apiEndpoint);
  const isStatCard = config.type === 'stat-card';
  const batchEnabled = isStatCard && !batchContext.isError;
  const batchEntry = batchEnabled ? batchContext.data?.[metricPath] : undefined;
  const useBatchFetch = batchEnabled && (batchContext.isLoading || !!batchContext.data);

  const { data, isLoading, error } = useMetrics({
    config,
    storeId,
    startDate,
    endDate,
    enabled: !useBatchFetch && config.id !== 'cyd-cohort-rate',
  });

  const displayData = batchEntry?.current ?? data;
  const displayLoading = useBatchFetch
    ? !batchEntry && batchContext.isLoading
    : isLoading;
  const displayError = batchContext.isError && useBatchFetch && !batchEntry
    ? batchContext.error
    : error;

  if (config.id === 'cyd-cohort-rate') {
    return (
      <CydCohortRateChart
        config={config}
        storeId={storeId}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }

  if (displayError) {
    return (
      <div className="metric-error">
        <p>Error cargando {config.title}: {displayError.message}</p>
      </div>
    );
  }

  return (
    <MetricRenderer
      config={config}
      data={displayData as any}
      loading={displayLoading}
      error={(displayError as any)?.message}
      storeId={storeId}
      startDate={startDate}
      endDate={endDate}
    />
  );
};

export default React.memo(MetricItem, (prevProps, nextProps) => {
  return (
    prevProps.config.id === nextProps.config.id &&
    prevProps.storeId === nextProps.storeId &&
    prevProps.startDate === nextProps.startDate &&
    prevProps.endDate === nextProps.endDate
  );
});
